import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage, LangSwitcher } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Camera, Check, X, Minus, Wifi, WifiOff, RefreshCw, LogOut, MapPin, ChevronLeft, ClipboardCheck, Trash2, FileDown } from "lucide-react";

export const downloadInspectionPdf = async (iid, lang = null) => {
    const l = lang || localStorage.getItem("apptrack_lang") || "en";
    const res = await api.get(`/field/inspections/${iid}/report.pdf?lang=${l}`, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
};

const OUTBOX_KEY = "field-outbox-v1";
const RESULT_STYLE = {
    pass: "bg-emerald-500 text-white",
    fail: "bg-rose-500 text-white",
    na: "bg-slate-400 text-white",
};
const OUTCOME_LABEL = { pass: "Passed", fail: "Failed", conditional: "Conditional pass" };
const STATUS_LABEL = { scheduled: "Scheduled", in_progress: "In progress", completed: "Completed" };

const readOutbox = () => { try { return JSON.parse(localStorage.getItem(OUTBOX_KEY) || "[]"); } catch { return []; } };
const writeOutbox = (items) => localStorage.setItem(OUTBOX_KEY, JSON.stringify(items));

export default function FieldApp() {
    const { user, logout } = useAuth();
    const { tr } = useLanguage();
    const navigate = useNavigate();
    const [assignments, setAssignments] = useState([]);
    const [sel, setSel] = useState(null);
    const [online, setOnline] = useState(navigator.onLine);
    const [outbox, setOutbox] = useState(readOutbox());
    const [syncing, setSyncing] = useState(false);
    const fileRef = useRef(null);

    const load = useCallback(() => {
        if (!navigator.onLine) return;
        api.get("/field/assignments").then(({ data }) => setAssignments(data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (user && user.role !== "inspector" && !["admin", "manager", "planner", "reviewer"].includes(user.role)) {
            navigate("/login", { replace: true });
        }
        load();
        const on = () => { setOnline(true); load(); };
        const off = () => setOnline(false);
        window.addEventListener("online", on);
        window.addEventListener("offline", off);
        return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
    }, []);

    const queueUpdate = (insp) => {
        const entry = { id: insp.id, checklist: insp.checklist, status: insp.status, outcome: insp.outcome, notes: insp.notes };
        const next = [...outbox.filter((o) => o.id !== insp.id || o.kind !== "update"), { kind: "update", ...entry }];
        setOutbox(next); writeOutbox(next);
    };

    const mutate = (insp, patch) => {
        const updated = { ...insp, ...patch };
        setSel(updated);
        setAssignments((a) => a.map((x) => (x.id === updated.id ? updated : x)));
        if (online) {
            return api.patch(`/field/inspections/${updated.id}`, patch).catch(() => queueUpdate(updated));
        }
        queueUpdate(updated);
        return Promise.resolve();
    };

    const setResult = (item, result) => {
        const checklist = sel.checklist.map((c) => (c.key === item.key ? { ...c, result: c.result === result ? "pending" : result } : c));
        mutate(sel, { checklist, status: sel.status === "scheduled" ? "in_progress" : sel.status });
    };

    const addPhoto = async (file) => {
        if (!file || !sel) return;
        const caption = "";
        const geo = await new Promise((res) => {
            if (!navigator.geolocation) return res({});
            navigator.geolocation.getCurrentPosition(
                (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
                () => res({}), { timeout: 4000 });
        });
        if (online) {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("caption", caption);
            if (geo.lat) { fd.append("lat", geo.lat); fd.append("lng", geo.lng); }
            fd.append("taken_at", new Date().toISOString());
            try {
                const { data } = await api.post(`/field/inspections/${sel.id}/photos`, fd);
                const updated = { ...sel, photos: [...(sel.photos || []), data] };
                setSel(updated);
                setAssignments((a) => a.map((x) => (x.id === updated.id ? updated : x)));
                toast.success(tr("Photo attached"));
                return;
            } catch { /* fall through to offline queue */ }
        }
        const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
        const entry = { kind: "photo", id: sel.id, tempId: `tmp-${Date.now()}`, filename: file.name, dataUrl, ...geo };
        const next = [...outbox, entry];
        setOutbox(next); writeOutbox(next);
        const updated = { ...sel, photos: [...(sel.photos || []), { id: entry.tempId, dataUrl, caption: "", offline: true }] };
        setSel(updated);
        toast.success(tr("Photo queued — will sync when online"));
    };

    const removePhoto = (pid) => {
        const p = (sel.photos || []).find((x) => x.id === pid);
        if (p?.offline) {
            const next = outbox.filter((o) => o.tempId !== pid);
            setOutbox(next); writeOutbox(next);
            const updated = { ...sel, photos: sel.photos.filter((x) => x.id !== pid) };
            setSel(updated);
            return;
        }
        api.delete(`/field/inspections/${sel.id}/photos/${pid}`).then(() => {
            const updated = { ...sel, photos: sel.photos.filter((x) => x.id !== pid) };
            setSel(updated);
        }).catch(() => toast.error(tr("Delete failed")));
    };

    const sync = async () => {
        if (!outbox.length || syncing) return;
        setSyncing(true);
        try {
            const updates = outbox.filter((o) => o.kind === "update");
            if (updates.length) await api.post("/field/sync", { inspections: updates });
            for (const ph of outbox.filter((o) => o.kind === "photo")) {
                const blob = await (await fetch(ph.dataUrl)).blob();
                const fd = new FormData();
                fd.append("file", new File([blob], ph.filename, { type: blob.type }));
                if (ph.lat) { fd.append("lat", ph.lat); fd.append("lng", ph.lng); }
                await api.post(`/field/inspections/${ph.id}/photos`, fd);
            }
            setOutbox([]); writeOutbox([]);
            toast.success(tr("All offline work synced"));
            load();
        } catch {
            toast.error(tr("Sync incomplete — will retry"));
        } finally {
            setSyncing(false);
        }
    };

    const complete = async (outcome) => {
        await mutate(sel, { status: "completed", outcome, completed_at: new Date().toISOString() });
        toast.success(`${tr("Inspection")} · ${tr(OUTCOME_LABEL[outcome])}`);
        setSel(null);
        load();
    };

    const done = (i) => (i.checklist || []).filter((c) => c.result !== "pending").length;

    return (
        <div className="min-h-screen bg-[#EEF3F7]" data-testid="field-app">
            <header className="bg-[#0A2540] text-white px-5 py-4 flex items-center justify-between gap-4 sticky top-0 z-10">
                <div className="flex items-center gap-3">
                    <img src="/assets/logo-light.png" alt="LandLogic NexusOne" className="h-8" />
                    <div>
                        <div className="font-heading font-bold text-sm leading-tight">{tr("Field Inspections")}</div>
                        <div className="text-[11px] text-white/60">{user?.name} · {tr("Building Services")}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <LangSwitcher />
                    <span data-testid="field-online-badge" className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full ${online ? "bg-emerald-400/15 text-emerald-300" : "bg-amber-400/15 text-amber-300"}`}>
                        {online ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}{online ? tr("Online") : tr("Offline")}
                    </span>
                    {outbox.length > 0 && (
                        <button data-testid="field-sync-btn" onClick={sync} disabled={!online || syncing}
                            className="inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full bg-[#00B4D8] text-white disabled:opacity-50">
                            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> {tr("Sync")} ({outbox.length})
                        </button>
                    )}
                    <button data-testid="field-logout-btn" onClick={() => { logout(); navigate("/login"); }} className="p-2 rounded-full hover:bg-white/10"><LogOut className="w-4 h-4" /></button>
                </div>
            </header>

            {!sel ? (
                <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-3" data-testid="field-assignments">
                    <h1 className="font-heading text-xl font-bold text-[#0A2540] mb-1">{tr("My assignments")}</h1>
                    {assignments.map((i) => (
                        <button key={i.id} data-testid={`field-assignment-${i.id.slice(0, 8)}`} onClick={() => setSel(i)}
                            className="w-full text-left bg-white rounded-[14px] border border-slate-200/70 p-5 shadow-sm active:scale-[0.99] transition-transform">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <span className={`text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full ${i.status === "completed" ? "bg-emerald-100 text-emerald-700" : i.status === "in_progress" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}`}>
                                    {tr(STATUS_LABEL[i.status] || i.status)}{i.outcome ? ` · ${tr(OUTCOME_LABEL[i.outcome])}` : ""}
                                </span>
                                <span className="text-xs text-slate-400 font-data">{i.scheduled_date}</span>
                            </div>
                            <div className="font-heading font-bold text-[#0A2540] text-lg mt-2">{tr(i.inspection_type)}</div>
                            <div className="text-sm text-slate-500 mt-0.5">{i.file_number} — {i.app_title}</div>
                            {i.permit_number && <div className="text-[11px] font-bold text-[#0092B0] mt-0.5">{tr("Building permit")} · {i.permit_number}</div>}
                            <div className="text-xs text-slate-400 mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{i.app_address}</div>
                            <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                <div className="h-full bg-[#00B4D8] rounded-full" style={{ width: `${(i.checklist?.length ? (done(i) / i.checklist.length) * 100 : 0)}%` }} />
                            </div>
                            <div className="text-[11px] text-slate-400 mt-1.5">{done(i)}/{(i.checklist || []).length} {tr("checklist items")} · {(i.photos || []).length} {tr("photos")}</div>
                        </button>
                    ))}
                    {!assignments.length && <div className="text-sm text-slate-400 text-center py-16" data-testid="field-empty">{tr("No inspections assigned.")}</div>}
                </main>
            ) : (
                <main className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="field-inspection-detail">
                    <button data-testid="field-back-btn" onClick={() => { setSel(null); load(); }} className="inline-flex items-center gap-1 text-sm font-bold text-[#0092B0] mb-3"><ChevronLeft className="w-4 h-4" /> {tr("Assignments")}</button>
                    <div className="bg-white rounded-[14px] border border-slate-200/70 p-5 shadow-sm">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                                <div className="font-heading font-bold text-[#0A2540] text-xl">{tr(sel.inspection_type)}</div>
                                <div className="text-sm text-slate-500 mt-0.5">{sel.file_number} — {sel.app_title}</div>
                                {sel.permit_number && <div className="text-[11px] font-bold text-[#0092B0] mt-0.5">{tr("Building permit")} · {sel.permit_number}</div>}
                                <div className="text-xs text-slate-400 mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{sel.app_address} · {sel.scheduled_date}</div>
                                {sel.notes && <div className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2 mt-2">{sel.notes}</div>}
                            </div>
                            {sel.app_location && (
                                <a data-testid="field-directions-link" className="brand-btn px-4 py-2 rounded-[8px] text-xs font-bold"
                                    target="_blank" rel="noreferrer" href={`https://www.google.com/maps/dir/?api=1&destination=${sel.app_location.lat},${sel.app_location.lng}`}>{tr("Directions")}</a>
                            )}
                            <button data-testid="field-report-btn" onClick={() => downloadInspectionPdf(sel.id).catch(() => toast.error("Report download failed"))}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-xs font-bold border border-slate-300 text-slate-600 hover:border-[#00B4D8] hover:text-[#0092B0] transition-colors">
                                <FileDown className="w-3.5 h-3.5" /> {tr("Report PDF")}
                            </button>
                        </div>
                    </div>

                    <h2 className="font-heading font-bold text-[#0A2540] mt-5 mb-2 flex items-center gap-2"><ClipboardCheck className="w-4 h-4" /> {tr("Checklist")}</h2>
                    <div className="space-y-2" data-testid="field-checklist">
                        {(sel.checklist || []).map((c) => (
                            <div key={c.key} className="bg-white rounded-[12px] border border-slate-200/70 p-4 shadow-sm" data-testid={`field-item-${c.key}`}>
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <span className="text-sm font-semibold text-slate-700 flex-1 min-w-[180px]">{tr(c.label)}</span>
                                    <div className="flex gap-1.5">
                                        {[["pass", Check], ["fail", X], ["na", Minus]].map(([r, Icon]) => (
                                            <button key={r} data-testid={`field-${c.key}-${r}`} onClick={() => setResult(c, r)}
                                                className={`w-11 h-11 rounded-[10px] flex items-center justify-center border transition-colors ${c.result === r ? RESULT_STYLE[r] + " border-transparent" : "border-slate-200 text-slate-400 hover:bg-slate-50"}`}>
                                                <Icon className="w-5 h-5" />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                {c.result === "fail" && (
                                    <input data-testid={`field-${c.key}-note`} value={c.note} placeholder={tr("Deficiency note…")}
                                        onChange={(e) => mutate(sel, { checklist: sel.checklist.map((x) => (x.key === c.key ? { ...x, note: e.target.value } : x)) })}
                                        className="mt-2.5 w-full rounded-[8px] border border-rose-200 bg-rose-50/50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300" />
                                )}
                            </div>
                        ))}
                    </div>

                    <h2 className="font-heading font-bold text-[#0A2540] mt-5 mb-2 flex items-center gap-2"><Camera className="w-4 h-4" /> {tr("Photos")} ({(sel.photos || []).length})</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="field-photos">
                        {(sel.photos || []).map((p) => (
                            <div key={p.id} className="relative rounded-[12px] overflow-hidden border border-slate-200 bg-slate-100 aspect-square" data-testid={`field-photo-${p.id.slice(0, 8)}`}>
                                {p.offline
                                    ? <img src={p.dataUrl} alt="Queued" className="w-full h-full object-cover" />
                                    : <img src={`${process.env.REACT_APP_BACKEND_URL}/api/field/inspections/${sel.id}/photos/${p.id}`} alt={p.caption || "Inspection"} className="w-full h-full object-cover" />}
                                {p.offline && <span className="absolute top-2 left-2 text-[9px] font-extrabold uppercase bg-amber-400 text-white px-1.5 py-0.5 rounded">{tr("Queued")}</span>}
                                <button data-testid={`field-photo-del-${p.id.slice(0, 8)}`} onClick={() => removePhoto(p.id)}
                                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                        ))}
                        <button data-testid="field-add-photo-btn" onClick={() => fileRef.current?.click()}
                            className="aspect-square rounded-[12px] border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1.5 text-slate-400 hover:border-[#00B4D8] hover:text-[#0092B0] transition-colors">
                            <Camera className="w-7 h-7" /><span className="text-[11px] font-bold">{tr("Take / add photo")}</span>
                        </button>
                        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="field-photo-input"
                            onChange={(e) => { addPhoto(e.target.files?.[0]); e.target.value = ""; }} />
                    </div>

                    {sel.status !== "completed" && (
                        <div className="mt-6 bg-white rounded-[14px] border border-slate-200/70 p-5 shadow-sm">
                            <div className="font-heading font-bold text-[#0A2540] mb-3">{tr("Complete inspection")}</div>
                            <div className="grid grid-cols-3 gap-2">
                                <button data-testid="field-complete-pass" onClick={() => complete("pass")} className="py-3.5 rounded-[10px] bg-emerald-500 text-white font-bold text-sm">{tr("Pass")}</button>
                                <button data-testid="field-complete-conditional" onClick={() => complete("conditional")} className="py-3.5 rounded-[10px] bg-amber-500 text-white font-bold text-sm">{tr("Conditional")}</button>
                                <button data-testid="field-complete-fail" onClick={() => complete("fail")} className="py-3.5 rounded-[10px] bg-rose-500 text-white font-bold text-sm">{tr("Fail")}</button>
                            </div>
                        </div>
                    )}
                </main>
            )}
        </div>
    );
}
